import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { superadminAPI } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Shield, Loader2, CheckCircle, XCircle, Eye, EyeOff, AlertTriangle } from 'lucide-react';

export default function AcceptSuperadminInvitationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token] = useState(() => searchParams.get('token') || window.history.state?.evaToken || '');

  // EIP-433: the token is a single-use secret that arrived in the URL, so it
  // must not stay in the address bar, the history entry, or any Referer this
  // page sends. Captured into state first so everything below keeps working,
  // then also parked in history.state so a refresh mid-form still has it
  // instead of landing on the invalid-link card. Raw replaceState rather than
  // the router's navigate: this rewrites the current entry only and leaves
  // react-router's own state (idx, key) intact by spreading it.
  useEffect(() => {
    if (!searchParams.get('token') || !token) return;
    window.history.replaceState(
      { ...window.history.state, evaToken: token },
      document.title,
      window.location.pathname
    );
  }, [searchParams, token]);
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState(false);

  // Password requirements
  const passwordRequirements = [
    { test: (p) => p.length >= 8, label: 'At least 8 characters' },
    { test: (p) => /[A-Z]/.test(p), label: 'One uppercase letter (A-Z)' },
    { test: (p) => /[a-z]/.test(p), label: 'One lowercase letter (a-z)' },
    { test: (p) => /\d/.test(p), label: 'One digit (0-9)' },
    { test: (p) => /[@$!%*?&#^()_+\-=[\]{}|;:,.<>]/.test(p), label: 'One special character' },
  ];

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided. Please check your invitation link.');
      setLoading(false);
      return;
    }

    const verifyToken = async () => {
      try {
        const result = await superadminAPI.verifySuperadminInvitation(token);
        setInvitation(result);
      } catch (err) {
        setError(err.response?.data?.detail || 'This invitation link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    const allRequirementsMet = passwordRequirements.every(req => req.test(password));
    if (!allRequirementsMet) {
      toast.error('Please meet all password requirements');
      return;
    }
    
    setSubmitting(true);
    try {
      await superadminAPI.acceptSuperadminInvitation(token, password);
      setSuccess(true);
      toast.success('Your superadmin account has been activated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900">
        <Card className="w-full max-w-md mx-4 bg-white/95 backdrop-blur">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600 mb-4" />
            <p className="text-slate-600">Verifying invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900">
        <Card className="w-full max-w-md mx-4 bg-white/95 backdrop-blur">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <XCircle className="h-12 w-12 text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Invalid Invitation</h2>
            <p className="text-slate-600 text-center mb-6">{error}</p>
            <Button onClick={() => navigate('/login')} variant="outline">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900">
        <Card className="w-full max-w-md mx-4 bg-white/95 backdrop-blur">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CheckCircle className="h-12 w-12 text-emerald-500 mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Account Activated!</h2>
            <p className="text-slate-600 text-center mb-6">
              Your superadmin account has been set up successfully. You can now log in.
            </p>
            <Button 
              onClick={() => navigate('/login')}
              className="bg-gradient-to-r from-slate-700 to-emerald-600 hover:from-slate-800 hover:to-emerald-700"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 p-4">
      <Card className="w-full max-w-md bg-white/95 backdrop-blur">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-full bg-gradient-to-r from-slate-700 to-emerald-600">
              <Shield className="h-8 w-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl">Accept Superadmin Invitation</CardTitle>
          <CardDescription>
            Set your password to activate your superadmin account
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {/* Invitation Info */}
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800">Superadmin Access</p>
                <p className="text-amber-700 mt-1">
                  You're being granted superadmin access which provides full control over all tenants and platform settings.
                </p>
              </div>
            </div>
          </div>
          
          <div className="mb-6 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Email:</span>
              <span className="font-medium text-slate-900">{invitation?.email}</span>
            </div>
            {invitation?.name && (
              <div className="flex justify-between">
                <span className="text-slate-500">Name:</span>
                <span className="font-medium text-slate-900">{invitation.name}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-slate-500">Invited by:</span>
              <span className="font-medium text-slate-900">{invitation?.invited_by}</span>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  className="pr-10"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            {/* Password Requirements */}
            <div className="space-y-1">
              {passwordRequirements.map((req, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs">
                  {req.test(password) ? (
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                  ) : (
                    <div className="h-3.5 w-3.5 rounded-full border border-slate-300" />
                  )}
                  <span className={req.test(password) ? 'text-emerald-600' : 'text-slate-500'}>
                    {req.label}
                  </span>
                </div>
              ))}
            </div>
            
            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  className="pr-10"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-red-500">Passwords do not match</p>
              )}
            </div>
            
            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-slate-700 to-emerald-600 hover:from-slate-800 hover:to-emerald-700"
              disabled={submitting || !password || password !== confirmPassword}
              data-testid="accept-invitation-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Activating...
                </>
              ) : (
                <>
                  <Shield className="h-4 w-4 mr-2" />
                  Activate Superadmin Account
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
